import { Component, OnInit, HostListener, Optional } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { MatFormField } from '@angular/material/form-field';

import {
   FormGroup, FormControl, FormArray,
} from '@angular/forms';

import { of } from 'rxjs';
import {
   startWith,
   tap,
   map,
   switchMap,
} from 'rxjs/operators';

import { ValidateFormFields } from '@library/common/functions';
import { AppConfigService } from '@library/app.config';
import {
   CountriesService,
   BreakpointService,
   BreadcrumbService,
   RecoveryService,
} from '@library/core/services';
import { AddressbookService } from '../addressbook.service';

import { Business, BusinessGroup } from '@library/common/models';
import { AddressBookComponent } from '../addressbook.component';

@Component({
   selector: 'app-business',
   templateUrl: './business.component.html',
   styleUrls: ['./business.component.scss']
})
export class BusinessComponent implements OnInit {
   businessForm: FormGroup = BusinessGroup();

   lengths;
   required;

   new = true;
   edit = false;

   prevType = '';
   nextId;
   prevId;

   loading = true;
   saving = false;
   deleting = false;

   @HostListener( 'document:keydown', ['$event'])
   handleKeyPress( event: KeyboardEvent ) {
      if ( event.key === 'Enter' && ( !this.businessForm.invalid ) ) {
         this.save();
      }
   }

   get id() { return this.businessForm.get( 'id' ).value; }
   get country() { return this.businessForm.get( 'country' ) as FormControl; }
   get postalCode() { return this.businessForm.get( 'postalcode' ) as FormControl; }
   get contacts(): FormArray { return this.businessForm.get('contacts') as FormArray; }
   get owned() { return this.businessForm.get( 'owned' ).value; }

   filter = 'all';
   
   constructor(
      private router: Router,
      private route: ActivatedRoute,
      private breadcrumbs: BreadcrumbService,
      private config: AppConfigService,
      private recover: RecoveryService,
      private addressbook: AddressbookService,

      public countries: CountriesService,
      public breakpoints: BreakpointService,
      @Optional() public parentComponent: AddressBookComponent,
   ) {
   }

   ngOnInit() {
      this.lengths = this.config.getProperty( this.config.getExtendedConfig( 'billingparty' ), 'maxLength' );
      this.required = this.config.getProperty( this.config.getExtendedConfig( 'billingparty' ), 'required' );

      this.route.params.pipe(
         startWith(),
         tap( _ => this.breadcrumbs.newCrumb({ label: 'Address Book', path: '/addressbook' }) ),
         tap( _ => this.loading = true ),
         tap( _ => this.new = false ),
         switchMap( params => {
            switch ( params.action ) {
               case 'recover':
                  return this.recover.recoverItem<Business>( params.id ).pipe(
                     tap( _ => this.breadcrumbs.addCrumble( 'recover' )),
                     map( b => ({ ...b, id: null })),
                  );
               case 'duplicate':
                  return this.addressbook.getBusiness( +params.id ).pipe(
                     tap( _ => this.breadcrumbs.addCrumble( 'duplicate' )),
                     map( b => ({ ...b, id: null })),
                  );
               case 'edit':
                  this.edit = true;
                  return this.addressbook.getBusiness( +params.id );
               default:
                  this.new = true;
                  return of( new Business() );
            }
         }),
         map( b => new Business( b )),
         tap( b => this.breadcrumbs.addCrumble( b.name || 'New' )),
         tap( b => this.businessForm = BusinessGroup( b )),
         tap( _ => this.load() ),
         tap( _ => this.loading = false ),
         tap( _ => {
            const abType = this.addressbook.filter$.getValue();
            
            if ( this.new ) {
               this.businessForm.get( 'type' ).setValue( abType === 'all' ? 'account' : abType );
               this.addressbook.filter$.subscribe(( f ) => {
                  this.businessForm.get( 'type' ).setValue( f === 'all' ? 'account' : f );

                  this.filter = f;
               });
            } else if ( this.edit ) {
               this.prevType = this.businessForm.get( 'type' ).value;
            }

            this.filter = abType;
         }),
         // we'll get a copy of the list from the parent component
         switchMap(_ => this.parentComponent?.ab$ ),
         tap(( b: Business[] ) => {
            const currentIndex = b.findIndex((v) => +v.id === +this.id);
   
            // Get the next and previous IDs
            this.nextId = b[currentIndex + 1]?.id;
            this.prevId = b[currentIndex - 1]?.id;
         }),
      ).subscribe();

   }

   filterChange( data: string ) {
      this.addressbook.changeFilter( data );

      if ( !this.new ) {
         this.router.navigate([ 'addressbook', 'new' ]);
      }
   }

   load() {
      // stripping out old 'USA' vs 'US' countries that may still live in the ab
      if ( this.country.value ) {
         this.country.setValue( this.country.value.substr( 0, 2 ));
      }

      if ( !this.new ) {
         ValidateFormFields( this.businessForm, true );
      }

      // watch the country input for change and update accordingly
      this.businessForm.get( 'country' ).valueChanges.pipe(
         startWith( this.businessForm.get( 'country' ).value ),
      ).subscribe( code => {
         // check the region
         this.countries.country( code ).subscribe( c => {
            if ( !c?.regions ) { return; }
            const regions: string[] = c.regions.map( r => r.code );
            if ( !regions.includes( this.businessForm.get( 'state' ).value )) {
               this.businessForm.get( 'state' ).setValue( '', { emitEvent: false });
            }
         });

         // check the postal code
         this.countries.setPostalCodeValidators( this.country.value, this.postalCode );
         if ( this.businessForm.get( 'postalcode' ).invalid ) {
            this.businessForm.get( 'postalcode' ).setValue( '', { emitEvent: false });
         }
      });
   }

   save() {
      this.saving = true;
      const business: Business = this.businessForm.value;
      this.businessForm.disable();
      this.contacts.disable();

      // remove "magic" contact
      business.contacts = business.contacts.slice( 0, -1 );

      // we'll need  a copy of this on this method as the fields will be overridden after the upsert
      const next_id = this.nextId;
      const prev_id = this.prevId;

      this.addressbook.upsertBusiness( business ).subscribe(
         b => {
            // check if the type is changed
            if ( this.edit && this.prevType !== business.type ) {
               if ( next_id == null && prev_id == null ) {
                  // if the list is already empty then we'll go to the create page
                  this.router.navigate([ 'redirect', 'addressbook/create' ] );
               } else if ( this.filter === 'all' ) {
                  this.router.navigate([ 'redirect', `addressbook/edit/${b.id}` ] );
               } else {
                  // we'll go to the next/previous item on the list
                  this.router.navigate([ 'redirect', `addressbook/edit/${next_id || prev_id}` ] );
               }
            } else {
               // check if the filter is not all and is not the same with the currently saved type
               if ( this.filter !== 'all' && this.filter !== business.type ) {
                  this.filterChange( business.type );
               }

               this.router.navigate([ 'redirect', `addressbook/edit/${b.id}` ] );
            }
         },
         e => {
            console.log( 'addressbook save error', e )
            this.saving = false;
            this.businessForm.enable();
            this.contacts.enable();
         }
      );
   }

   delete() {
      this.loading = true;
      this.addressbook.deleteBusiness( this.id )
         .subscribe( _ => this.router.navigate([ 'redirect', 'addressbook' ]));
   }

   focused( field: MatFormField ): boolean {
      return field && field._control.focused;
   }

}
